import apiClient, { type ApiError } from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import { API_ENDPOINTS } from '@/lib/api/config'

export {
  gatewayMetaLookup,
  getPaymentLogDetail,
  normalizeGatewayStatusMetaMap,
  getGatewayStatuses,
  generateChargebackPacket,
} from '@/lib/api/paymentLogService'

import type {
  DefaultPaymentLogListResponse,
  DefaultPaymentLogUpdatePayload,
  ChargebackNote,
  TotalContractListResponse,
  RevenuePaymentLogListResponse,
  MessageLogListResponse,
  ImpersonateLogListResponse,
  FinancialDailyReportListResponse,
  GenericReportListResponse,
  MailTemplateOption,
  LegacyMailLogListResponse,
  MailLogDetailVariant,
  MailLogDetail,
  MailTraceApplicantSearchRow,
  OutstandingBalanceListResponse,
  OutstandingBalanceApplicationInfo,
  InvoiceReminderPayload,
  ApiLogListResponse,
  ApiLogDetail,
  MrgReportListResponse,
  WeeklyReimbursementListResponse,
  PmcPerformanceListResponse,
  SalesTeamOption,
  ChargebackApplicationInfo,
} from '../types/api'

/** Row from `GET /api/default_payment_log` (Angular `ReportsDefaultPaymentLogsComponent`). */


/**
 * Paginated default payment logs — same API as Angular reports default payment logs.
 * Pass a fully-built query string (supports repeated `status[]` like the legacy app).
 */
export async function getDefaultPaymentLogList(
  searchParams: URLSearchParams,
): Promise<DefaultPaymentLogListResponse> {
  try {
    const qs = searchParams.toString()
    const url = qs
      ? `/api/default_payment_log?${qs}`
      : '/api/default_payment_log'
    const response = await apiClient.get<DefaultPaymentLogListResponse>(url)
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(
        error,
        'Failed to load default payment logs.',
      ),
      status: (error as ApiError).status,
    } as ApiError
  }
}


export async function updateDefaultPaymentLog(
  paymentLogId: string | number,
  payload: DefaultPaymentLogUpdatePayload,
): Promise<void> {
  try {
    await apiClient.post(`/api/update_default_payment/${paymentLogId}`, payload)
  } catch (error) {
    throw {
      message: extractErrorMessage(
        error,
        'Failed to update default payment information.',
      ),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function completeDefaultPaymentReimbursement(
  paymentLogId: string | number,
  payload: Pick<DefaultPaymentLogUpdatePayload, 'check_number' | 'payment_date'>,
): Promise<void> {
  try {
    await apiClient.post(`/api/update_payment/${paymentLogId}`, payload)
  } catch (error) {
    throw {
      message: extractErrorMessage(
        error,
        'Failed to complete reimbursement.',
      ),
      status: (error as ApiError).status,
    } as ApiError
  }
}


/**
 * Triggers a refresh from the payment processor (CardConnect).
 * Angular: GET `/api/payment_gateway_status/:paymentId` then toast + navigate back.
 */
/** Row from `GET /api/total_contract` (Angular `ReportsTotalNumberContractComponent`). */


/** Paginated total contracts — same API as Angular `loadPaymentlogs`. */
export async function getTotalContractList(
  searchParams: URLSearchParams,
): Promise<TotalContractListResponse> {
  try {
    const qs = searchParams.toString()
    const url = qs ? `/api/total_contract?${qs}` : '/api/total_contract'
    const response = await apiClient.get<TotalContractListResponse>(url)
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load total contracts.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/** Row from `GET /api/payment_log` (Angular `ReportsPaymentLogsComponent`). */


/** Paginated revenue payment logs — same API as Angular payment logs report. */
export async function getRevenuePaymentLogList(
  searchParams: URLSearchParams,
): Promise<RevenuePaymentLogListResponse> {
  try {
    const qs = searchParams.toString()
    const url = qs ? `/api/payment_log?${qs}` : '/api/payment_log'
    const response = await apiClient.get<RevenuePaymentLogListResponse>(url)
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(
        error,
        'Failed to load revenue payment logs.',
      ),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/** Row from `GET /api/twilio_logs` (Angular Message Logs report). */


export async function getMessageLogList(
  searchParams: URLSearchParams,
): Promise<MessageLogListResponse> {
  try {
    const qs = searchParams.toString()
    const url = qs ? `/api/twilio_logs?${qs}` : '/api/twilio_logs'
    const response = await apiClient.get<MessageLogListResponse>(url)
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load message logs.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/** Row from `GET /api/impersonate_audit` (Angular Impersonate Logs report). */


export async function getImpersonateLogList(
  searchParams: URLSearchParams,
): Promise<ImpersonateLogListResponse> {
  try {
    const qs = searchParams.toString()
    const url = qs ? `/api/impersonate_audit?${qs}` : '/api/impersonate_audit'
    const response = await apiClient.get<ImpersonateLogListResponse>(url)
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load impersonate logs.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}




async function getFinancialDailyReportListByPath(
  path: string,
  searchParams: URLSearchParams,
  failureMessage: string,
): Promise<FinancialDailyReportListResponse> {
  try {
    const qs = searchParams.toString()
    const url = qs ? `${path}?${qs}` : path
    const response = await apiClient.get<FinancialDailyReportListResponse>(url)
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, failureMessage),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export function getFinancialDailyReportList(
  searchParams: URLSearchParams,
): Promise<FinancialDailyReportListResponse> {
  return getFinancialDailyReportListByPath(
    '/api/report/daily_payment_extended',
    searchParams,
    'Failed to load financial daily report.',
  )
}

export function getFinancialDailyDefaultsReportList(
  searchParams: URLSearchParams,
): Promise<FinancialDailyReportListResponse> {
  return getFinancialDailyReportListByPath(
    '/api/report/daily_payment_default_extended',
    searchParams,
    'Failed to load financial daily defaults report.',
  )
}

export async function getGenericReportList<Row extends Record<string, unknown>>(
  path: string,
  searchParams: URLSearchParams,
  failureMessage: string,
): Promise<GenericReportListResponse<Row>> {
  try {
    const qs = searchParams.toString()
    const url = qs ? `${path}?${qs}` : path
    const response = await apiClient.get<GenericReportListResponse<Row>>(url)
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, failureMessage),
      status: (error as ApiError).status,
    } as ApiError
  }
}


export async function getMailTemplateOptions(
  search = '',
): Promise<MailTemplateOption[]> {
  try {
    const params = new URLSearchParams()
    if (search.trim()) params.set('q', search.trim())
    const qs = params.toString()
    const response = await apiClient.get<unknown>(
      `/api/all_template_id${qs ? `?${qs}` : ''}`,
    )
    const raw =
      response.data &&
      typeof response.data === 'object' &&
      'data' in response.data
        ? (response.data as { data: unknown }).data
        : response.data
    const rows = Array.isArray(raw) ? raw : Object.entries(raw ?? {})
    return rows
      .map((row) => {
        if (Array.isArray(row)) {
          return { value: String(row[0]), label: String(row[1] ?? row[0]) }
        }
        if (row && typeof row === 'object') {
          const record = row as Record<string, unknown>
          const value =
            record.value ??
            record.template_id ??
            record.id ??
            record.key ??
            record.lr_form
          const label =
            record.label ??
            record.template_name ??
            record.name ??
            record.title ??
            value
          return value == null
            ? null
            : { value: String(value), label: String(label ?? value) }
        }
        return row == null ? null : { value: String(row), label: String(row) }
      })
      .filter((option): option is MailTemplateOption => option != null)
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load LR form options.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function getLegacyMailLogList<Row extends Record<string, unknown>>(
  searchParams: URLSearchParams,
): Promise<LegacyMailLogListResponse<Row>> {
  try {
    const qs = searchParams.toString()
    const url = qs ? `/api/mail_logs?${qs}` : '/api/mail_logs'
    const response = await apiClient.get<LegacyMailLogListResponse<Row>>(url)
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load mail logs.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}




function mailLogDetailPath(variant: MailLogDetailVariant, id: string | number) {
  if (variant === 'mailgun') return `/api/mailgun_logs/${id}`
  if (variant === 'imap') return `/api/mailtrace/${id}`
  return `/api/mail_logs/${id}`
}

export async function getMailLogDetail(
  variant: MailLogDetailVariant,
  id: string | number,
): Promise<MailLogDetail> {
  try {
    const response = await apiClient.get<MailLogDetail>(
      mailLogDetailPath(variant, id),
      variant === 'legacy' ? { params: { type: 'database' } } : undefined,
    )
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load mail detail.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

function normalizeApplicantSearchRows(payload: unknown): MailTraceApplicantSearchRow[] {
  if (Array.isArray(payload)) return payload as MailTraceApplicantSearchRow[]
  if (payload && typeof payload === 'object') {
    const data = (payload as { data?: unknown }).data
    return Array.isArray(data) ? (data as MailTraceApplicantSearchRow[]) : []
  }
  return []
}

export async function searchMailTraceApplicants(
  search: string,
): Promise<MailTraceApplicantSearchRow[]> {
  try {
    const response = await apiClient.get<unknown>('/api/application', {
      params: { search },
    })
    return normalizeApplicantSearchRows(response.data)
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to search applicants.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function linkMailTraceApplicant(
  mailId: string | number,
  applicantId: string | number,
): Promise<void> {
  try {
    await apiClient.post(`/api/mailtrace/${mailId}/link`, {
      applicant_id: applicantId,
    })
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to link applicant.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function resendMailLog(
  variant: Exclude<MailLogDetailVariant, 'imap'>,
  id: string | number,
  to?: string,
): Promise<string> {
  try {
    const payload: Record<string, string> = {}
    if (to?.trim()) payload.to = to.trim()
    if (variant === 'legacy') payload.type = 'database'
    const response = await apiClient.post<string | { message?: string }>(
      mailLogDetailPath(variant, id),
      payload,
    )
    const data = response.data
    return typeof data === 'string' ? data : (data.message ?? 'Mail sent.')
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to send mail.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}


/** Row from `GET /api/payment/outstanding` (Angular Outstanding/Adjustment report). */


export async function getOutstandingBalanceList(
  searchParams: URLSearchParams,
): Promise<OutstandingBalanceListResponse> {
  try {
    const qs = searchParams.toString()
    const url = qs
      ? `/api/payment/outstanding?${qs}`
      : '/api/payment/outstanding'
    const response = await apiClient.get<OutstandingBalanceListResponse>(url)
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load balance report.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}


export async function getOutstandingBalanceApplicationInfo(
  applicantId: string | number,
): Promise<OutstandingBalanceApplicationInfo> {
  try {
    const response = await apiClient.get<OutstandingBalanceApplicationInfo>(
      `/api/application_info/${applicantId}`,
      { params: { payment_info: true } },
    )
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(
        error,
        'Failed to load application payment information.',
      ),
      status: (error as ApiError).status,
    } as ApiError
  }
}


export async function sendInvoiceReminder(
  applicantId: string | number,
  payload: InvoiceReminderPayload,
): Promise<void> {
  try {
    await apiClient.get(`/api/invoice_reminder/${applicantId}`, {
      params: {
        application_status: 'INVOICE',
        ...payload,
      },
    })
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to send invoice reminder.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/** Row from `GET /api/api_logs` (Angular `ReportsApiLogsComponent`). */



export async function getApiLogList(
  searchParams: URLSearchParams,
): Promise<ApiLogListResponse> {
  try {
    const qs = searchParams.toString()
    const url = qs ? `/api/api_logs?${qs}` : '/api/api_logs'
    const response = await apiClient.get<ApiLogListResponse>(url)
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load API logs.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function getApiLogDetail(
  logId: string | number,
): Promise<ApiLogDetail> {
  try {
    const response = await apiClient.get<ApiLogDetail>(`/api/api_logs/${logId}`)
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load API log detail.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function refreshPaymentGatewayStatus(
  paymentLogId: string | number,
): Promise<void> {
  try {
    await apiClient.get(API_ENDPOINTS.PAYMENT_GATEWAY_STATUS_SYNC(paymentLogId))
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Could not update gateway status.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/** Row from `GET /api/insurance_report` → `weekdata` (Angular MRG Report). */


export async function getMrgReportList(
  searchParams: URLSearchParams,
): Promise<MrgReportListResponse> {
  try {
    const qs = searchParams.toString()
    const url = qs ? `/api/insurance_report?${qs}` : '/api/insurance_report'
    const response = await apiClient.get<MrgReportListResponse>(url)
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load MRG report.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/** Weekly row from `GET /api/claim_insurance`. */


export async function getWeeklyReimbursementList(
  searchParams: URLSearchParams,
): Promise<WeeklyReimbursementListResponse> {
  try {
    const qs = searchParams.toString()
    const url = qs ? `/api/claim_insurance?${qs}` : '/api/claim_insurance'
    const response = await apiClient.get<WeeklyReimbursementListResponse>(url)
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(
        error,
        'Failed to load weekly reimbursements report.',
      ),
      status: (error as ApiError).status,
    } as ApiError
  }
}




export async function getSalesTeamsForPmcReport(): Promise<SalesTeamOption[]> {
  try {
    const response = await apiClient.get<
      SalesTeamOption[] | Record<string, SalesTeamOption>
    >('/api/sales_team', { params: { from_page: 'pmc_report' } })
    const data = response.data
    if (Array.isArray(data)) return data
    if (data && typeof data === 'object') {
      return Object.values(data)
    }
    return []
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load sales teams.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function getPmcPerformanceList(
  searchParams: URLSearchParams,
): Promise<PmcPerformanceListResponse> {
  try {
    const qs = searchParams.toString()
    const url = qs ? `/api/pmc_performance?${qs}` : '/api/pmc_performance'
    const response = await apiClient.get<PmcPerformanceListResponse>(url)
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(
        error,
        'Failed to load PMC performance report.',
      ),
      status: (error as ApiError).status,
    } as ApiError
  }
}


export async function getChargebackApplicationInfo(
  applicantId: string | number,
): Promise<ChargebackApplicationInfo> {
  try {
    const response = await apiClient.get<ChargebackApplicationInfo>(
      `/api/application_info/${applicantId}`,
      { params: { payment_info: true, current_address: true } },
    )
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(
        error,
        'Failed to load chargeback application information.',
      ),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function updateChargebackPaymentAdjustment(
  paymentAdjustmentId: string | number,
  payload: Record<string, unknown>,
): Promise<void> {
  try {
    await apiClient.post(`/api/payment_adjustment/${paymentAdjustmentId}`, payload)
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to update chargeback details.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/**
 * Angular `submitForm()` — executive review / payment approval.
 * DEFAULT payments already Completed post to `update_payment` instead.
 */
export async function submitPaymentExecAuth(
  paymentLogId: string | number,
  payload: Record<string, unknown>,
  useUpdatePayment = false,
): Promise<void> {
  const url = useUpdatePayment
    ? `/api/update_payment/${paymentLogId}`
    : `/api/payment_auth_exec/${paymentLogId}`
  try {
    await apiClient.post(url, payload)
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to update payment status.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/** Angular `updateCheck()` — ACH / check completion. */
export async function updatePaymentCheck(
  paymentLogId: string | number,
  payload: Record<string, unknown>,
): Promise<void> {
  try {
    await apiClient.post(`/api/update_check/${paymentLogId}`, payload)
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to update check details.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

/** Angular `submitNotesForm()` — external-payment note. */
export async function submitPaymentRefundNote(
  paymentLogId: string | number,
  payload: Record<string, unknown>,
): Promise<void> {
  try {
    await apiClient.post(`/api/payment_refund/${paymentLogId}`, payload)
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to save note.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

function normalizeChargebackNotesPayload(payload: unknown): ChargebackNote[] {
  if (payload == null) return []
  if (Array.isArray(payload)) return payload as ChargebackNote[]
  if (typeof payload === 'object') {
    const o = payload as Record<string, unknown>
    if (Array.isArray(o.data)) return o.data as ChargebackNote[]
    if (Array.isArray(o.notes)) return o.notes as ChargebackNote[]
    if (Array.isArray(o.rows)) return o.rows as ChargebackNote[]
  }
  return []
}

export async function getChargebackNotes(
  paymentAdjustmentId: string | number,
): Promise<ChargebackNote[]> {
  try {
    const response = await apiClient.get(`/api/notes/${paymentAdjustmentId}`, {
      params: { notes_for: 'CHARGEBACK' },
    })
    return normalizeChargebackNotesPayload(response.data)
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to load chargeback notes.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function addChargebackNote(
  paymentAdjustmentId: string | number,
  notes: string,
): Promise<void> {
  try {
    await apiClient.post(`/api/notes/${paymentAdjustmentId}`, {
      notes,
      notes_for: 'CHARGEBACK',
    })
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to add chargeback note.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function uploadChargebackFiles(
  paymentAdjustmentId: string | number,
  fieldName: 'investigation_file' | 'decision_file',
  files: Array<{ file: File; checkType: string }>,
): Promise<unknown> {
  try {
    const formData = new FormData()
    files.forEach(({ file, checkType }, index) => {
      formData.append(`${fieldName}[${index}]`, file)
      formData.append(`check_types[${index}]`, checkType)
    })
    const response = await apiClient.post(
      `/api/chargeback/${paymentAdjustmentId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return response.data
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to upload chargeback file.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function deleteChargebackFile(
  attachmentId: string | number,
): Promise<void> {
  try {
    await apiClient.delete(`/api/chargeback/${attachmentId}`)
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to remove chargeback file.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}

export async function triggerReportExport(
  pathWithQuery: string,
  _searchParams?: URLSearchParams,
): Promise<{ message?: string }> {
  const path = pathWithQuery.startsWith('/')
    ? pathWithQuery
    : `/${pathWithQuery}`
  const response = await apiClient.get<{ message?: string } | string>(path)
  const data = response.data
  if (typeof data === 'string') return { message: data }
  if (data && typeof data === 'object') return data
  return {}
}

export async function getLatestReportExportStatus(
  userId: string | number,
  reportType: string,
): Promise<{ status?: string; file_path?: string; created_at?: string }> {
  const response = await apiClient.get<{
    status?: string
    file_path?: string
    created_at?: string
  }>(`/api/export/get_letest_report_for_user/${userId}`, {
    params: { report_type: reportType },
  })
  return response.data
}

/** Active Invites Status export — Angular `ReportsInvitesComponent.export`. */
export async function exportInviteeList(filters: {
  page?: number
  perPage?: number
  sortBy?: string
  orderBy?: string
  q?: string
  org_id?: string
  company_id?: string
  property_id?: string
  fromDt?: string
  toDt?: string
  inviteStatus?: string[]
}): Promise<string> {
  try {
    const params = new URLSearchParams()
    if (filters.page) params.set('page', String(filters.page))
    if (filters.perPage) params.set('perPage', String(filters.perPage))
    if (filters.sortBy) params.set('sortBy', filters.sortBy)
    if (filters.orderBy) params.set('orderBy', filters.orderBy)
    if (filters.q) params.set('q', filters.q)
    if (filters.org_id) params.set('org_id', filters.org_id)
    if (filters.company_id) params.set('company_id', filters.company_id)
    if (filters.property_id) params.set('property_id', filters.property_id)
    if (filters.fromDt) params.set('fromDt', filters.fromDt)
    if (filters.toDt) params.set('toDt', filters.toDt)
    if (filters.inviteStatus?.length) {
      for (const status of filters.inviteStatus) {
        if (status) params.append('inviteStatus[]', status)
      }
    }
    params.set('export', 'true')
    const response = await apiClient.get<string>(
      `${API_ENDPOINTS.INVITE_LIST}?${params.toString()}`,
    )
    const url = response.data
    return typeof url === 'string' ? url : ''
  } catch (error) {
    throw {
      message: extractErrorMessage(error, 'Failed to export invites.'),
      status: (error as ApiError).status,
    } as ApiError
  }
}
